import { BadgeAppearance, DEFAULT_BADGE_APPEARANCE, OrganisationPreferenceKey } from '@/enum/global.enum'
import { useOrganisationPreferences } from '@/hooks/use-organisation-preference'
import { useTheme } from '@/hooks/use-theme'
import { getBadgeColourStyle } from '@/utils/color.utils'
import { useCallback, useMemo } from 'react'

/**
 * Resolves the organisation-wide badge appearance preference and turns a colour into
 * the matching inline style for the active theme.
 *
 * Every colour-coded status, rating or priority badge in the platform reads this so a
 * single organisation setting flips them all between filled and outline. The colour is
 * also contrast-corrected against the current theme, so a pale colour stays visible on
 * the light theme and a dark one on the dark theme. Prefer the `ColourBadge` component;
 * use this hook directly only where a badge cannot be used (PDF exports, canvas
 * rendering, third-party widgets).
 *
 * @example
 * const { appearance, getColourStyle } = useBadgeAppearance()
 * <span style={getColourStyle(rating.color)}>{rating.name}</span>
 */
export const useBadgeAppearance = () => {
  const { getOrganisationPreferenceValue, isLoading } = useOrganisationPreferences()
  const { theme } = useTheme()

  const appearance = getOrganisationPreferenceValue<BadgeAppearance>(
    OrganisationPreferenceKey.BADGE_APPEARANCE,
    DEFAULT_BADGE_APPEARANCE
  )

  const isFilled = appearance === BadgeAppearance.FILLED

  const getColourStyle = useCallback(
    (colour?: string | null, colourAppearance: BadgeAppearance = appearance) =>
      getBadgeColourStyle(colour, colourAppearance, theme),
    [appearance, theme]
  )

  return useMemo(
    () => ({ appearance, isFilled, getColourStyle, isLoading, theme }),
    [appearance, isFilled, getColourStyle, isLoading, theme]
  )
}
